import React, { useEffect, useState } from "react";
import { CodeBlock } from "@atlaskit/code";
import CopyIcon from "@rsuite/icons/Copy";
import { Col, Grid, IconButton, Panel, Row, Loader, Message } from "rsuite";
import {
  chPostRequest,
  apiGetRequest,
  queryPostRequest,
  saveUserSession,
} from "../../api";
import "./QueryEditorPageUtil.css";
import { message } from "antd";
import axios from "axios";
import { format } from "sql-formatter";

export const QUERY_EDITOR_STATE_KEY = "maple.qe.state.v1";

export const readQueryEditorState = () => {
  try {
    const raw = window.sessionStorage.getItem(QUERY_EDITOR_STATE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return parsed;
  } catch {
    return null;
  }
};

export const writeQueryEditorState = (state) => {
  window.sessionStorage.setItem(
    QUERY_EDITOR_STATE_KEY,
    JSON.stringify(state)
  );
};

export const getUserId = () => {
  let user = JSON.parse(sessionStorage.getItem("user"));
  if (!user) {
    saveUserSession();
    user = JSON.parse(sessionStorage.getItem("user"));
  }
  return user ? user.user_id : null;
};

const getTextWidth = (() => {
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");

  return (text, font = "bold 14px Arial") => {
    if (!text || !context) return 0;
    context.font = font;
    return context.measureText(text).width;
  };
})();

export const calculateColumnWidths = (result, tableRef) => {
  if (!result || result.length === 0) {
    return [];
  }

  const CELL_PADDING = 15;
  const DEFAULT_MIN_WIDTH = 150;

  const totalTableWidth = tableRef?.current?.offsetWidth || 0;
  const keys = Object.keys(result[0]);

  const totalColumnWidth = keys.reduce((acc, key) => {
    const textWidth = getTextWidth(key) + CELL_PADDING;
    return acc + Math.max(textWidth, DEFAULT_MIN_WIDTH);
  }, 0);

  const extraSpacePerColumn =
    totalColumnWidth < totalTableWidth
      ? (totalTableWidth - totalColumnWidth) / keys.length
      : 0;

  return keys.map((key) => {
    const minimumColumnWidth = getTextWidth(key) + CELL_PADDING;
    const baseColumnWidth = Math.max(minimumColumnWidth, DEFAULT_MIN_WIDTH);
    const columnWidth = baseColumnWidth + extraSpacePerColumn;
    return { key, columnWidth, minimumColumnWidth };
  });
};

export const fetchQueryHistory = async (queryHistoryRef) => {
  try {
    const userId = getUserId();
    const data = await queryPostRequest("get_query_history", {
      user_id: userId,
    });
    const queries = data.query_history.map((item) => ({
      query: item.query,
      datasource: item.datasource,
    }));
    queryHistoryRef.current = queries;
  } catch (error) {
    console.error("Error fetching query history:", error);
  }
};

export const formatBytes = (bytes) => {
  if (bytes === 0) return "0 Bytes";
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return parseFloat((bytes / Math.pow(1024, i)).toFixed(2)) + " " + sizes[i];
};

export const fetchDatasources = async (setDatasources, setNoAccessDS, setAccessDS) => {
  try {
    const data = await apiGetRequest("/qe-datasources");
    let { allDatasources, DsAccess } = data;

    if (!DsAccess || DsAccess.length === 0) {
      sessionStorage.setItem(
        "loginMessage",
        "You don't have access to any datasources. Please contact the MAPLE team to know how you can get access."
      );
      window.location.href = "/login";
      return;
    }

    const rogers_ds = ["rogers", "rogers_video_player", "shaw", "shaw_video_player"];
    const videotron_ds = ["videotron", "videotron_video_player"];

    if (DsAccess.includes("cdn_rogers")) {
      DsAccess = [...DsAccess, ...rogers_ds];
    }
    if (DsAccess.includes("cdn_videotron")) {
      DsAccess = [...DsAccess, ...videotron_ds];
    }

    const filteredDsAccess = DsAccess.filter(ds => allDatasources.includes(ds));
    filteredDsAccess.sort();

    // Check for "cdn_rogers" or "cdn_videotron" in DsAccess
    if (DsAccess.includes("cdn_rogers")) {
      const rogersDataSources = rogers_ds.map((item, index) => ({
        label: item,
        value: item,
        key: `datasource-key-${index}`,
      }));
      setDatasources(rogersDataSources);
      setAccessDS(rogers_ds[0]);
    } else if (DsAccess.includes("cdn_videotron")) {
      const videotronDataSources = videotron_ds.map((item, index) => ({
        label: item,
        value: item,
        key: `datasource-key-${index}`,
      }));
      setDatasources(videotronDataSources);
      setAccessDS(videotron_ds[0]);
    } else {
      // Default behavior: format allDatasources
      const formattedDatasources = allDatasources
        .map((item, index) => ({
          label: item,
          value: item,
          key: `datasource-key-${index}`,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
      setDatasources(formattedDatasources);
      if (filteredDsAccess.length > 0) {
        setAccessDS(filteredDsAccess[0]);
      }
    }

    if (DsAccess.includes("operations")) {
      setNoAccessDS(null);
    } else {
      const noAccessDS = allDatasources.filter((ds) => !DsAccess.includes(ds));
      setNoAccessDS(noAccessDS);
    }
  } catch (error) {
    console.error("Error fetching datasources:", error);
  }
};

const simplifyType = (type) => {
  if (type === undefined) {
    return null;
  }
  if (type.startsWith("AggregateFunction")) {
    return "AggregateFunction";
  }
  return type.replace(/\s*\(.*?\)\s*/, "");
};

const fetchData = async (datasource, table, sql) => {
  const requestBody = {
    user_id: getUserId(),
    datasource: datasource,
    sql: sql,
  };

  try {
    const response = await chPostRequest("sql", requestBody);
    return response.results?.[0]?.body || null;
  } catch (error) {
    console.error("Error fetching data:", error);
    throw error;
  }
};

const fetchTableProperties = async (datasource, table) => {
  return await chPostRequest("table_properties", {
    user_id: getUserId(),
    datasource,
    table
  });
};

const fetchColumnDetails = async (datasource, table) => {
  return await chPostRequest("column_details", {
    user_id: getUserId(),
    datasource,
    table
  });
};

const fetchViewAndMVs = async (datasource, tables) => {
  try {
    const requestBody = {
      datasource: datasource,
      tables: tables
    };
    const response = await chPostRequest("view_mv_tables", requestBody);
    return response;
  } catch (error) {
    console.error("Error checking view and mv tables", error);
    return false;
  }
};

function formatNumber(num) {
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

export const ColumnPropsComponent = ({ datasource, table }) => {
  const [colProperties, setColProperties] = useState([]);

  useEffect(() => {
    const fetchColProperties = async () => {
      try {
        if (!datasource || !table) return;

        let tableName = table;
        if (
          datasource === "clo" &&
          (table === "access_logs" || table === "general_logs")
        ) {
          tableName = `${table}_v2`;
        }

        const response = await fetchColumnDetails(datasource, tableName);

        if (response && response.columns) {
          const formattedProperties = (response.columns || [])
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((col) => ({
              colName: col.name,
              colType: simplifyType(col.type),
              colComment:
                col.comment && col.comment.trim() !== ""
                  ? col.comment
                  : "No description",
              colSize: formatBytes(parseInt(col.compressedBytes || 0)),
            }))
            .filter((property) => property.colName);
          
          setColProperties(formattedProperties);
        }
      } catch (error) {
        console.error("Error fetching column properties:", error);
      }
    };

    fetchColProperties();
  }, [datasource, table]);

  return (
    <Grid className="properties-panel" fluid>
      {colProperties.map((property, index) => (
        <Row key={index} className="column-properties">
          <Col xs={6} className="property-colName">
            <span 
              className="column-info" 
              title={property.colName}
            >
              {property.colName}
            </span>
          </Col>
          <Col xs={6} className="property-colSize">
            <span 
              className="column-info" 
              title={property.colType}
            >
              {property.colType}
            </span>
          </Col>
          <Col xs={6} className="property-colSize">
            <span 
              className="column-info" 
              title={property.colComment}
            >
              {property.colComment}
            </span>
          </Col>
          <Col xs={6} className="property-colType">
            <span 
              className="column-info" 
              title={property.colSize}
            >
              {property.colSize}
            </span>
          </Col>
        </Row>
      ))}
    </Grid>
  );
};

export const PropertiesBlockComponent = ({ datasource, table }) => {
  const [properties, setProperties] = useState({
    engine: null,
    sortingKey: null,
    primaryKey: null,
    totalRows: null,
    totalBytes: null,
    totalBytesUncompressed: null,
    description: null,
  });
  
  useEffect(() => {
    const fetchProperties = async () => {
      try {
        if (!datasource || !table) return;

        let tableName = table;
        if (datasource === "clo" && 
            (table === "access_logs" || table === "general_logs")) {
          tableName = `${table}_v2`;
        }

        const response = await fetchTableProperties(datasource, tableName);
        
        if (response) {
          const basic = response.basicProperties;

          const displayProps = response.distributed && response.distributedProperties
            ? {
                ...basic,
                totalRows: response.distributedProperties.totalRows,
                totalBytes: response.distributedProperties.totalBytes,
                totalBytesUncompressed: response.distributedProperties.totalBytesUncompressed
              }
            : basic;

          setProperties({
            engine: displayProps.engine ?? "N/A",
            sortingKey: displayProps.sortingKey ?? "N/A",
            primaryKey: displayProps.primaryKey ?? "N/A",
            totalRows: displayProps.totalRows != null
              ? formatNumber(displayProps.totalRows) 
              : "N/A",
            totalBytes: displayProps.totalBytes 
              ? formatBytes(Number(displayProps.totalBytes)) 
              : "N/A",
            totalBytesUncompressed: displayProps.totalBytesUncompressed
              ? formatBytes(Number(displayProps.totalBytesUncompressed))
              : "N/A",
            description: displayProps.comment && displayProps.comment.trim() !== ""
              ? displayProps.comment
              : "No description found",
          });
        }
      } catch (error) {
        console.error("Error fetching table properties:", error);
      }
    };
  
    fetchProperties();
  }, [datasource, table]);

  return (
    <Panel header="Properties" className="properties-panel">
      <div className="properties-table">
        <div className="property">
          <span className="property-name">Engine</span>
          {properties.engine !== null ? (
            properties.engine
          ) : (
            <Loader size="xs" />
          )}
        </div>

        <div className="property">
          <span className="property-name">Description</span>
          {properties.description !== null ? (
            <span title={properties.description} className="property-value">
              {properties.description.length > 30
                ? `${properties.description.slice(0, 30)}...`
                : properties.description}
            </span>
          ) : (
            <Loader size="xs" />
          )}
        </div>

        <div className="property">
          <span className="property-name">Sorting key</span>
          {properties.sortingKey !== null ? (
            <span title={properties.sortingKey} className="property-value">
              {properties.sortingKey.length > 30
                ? `${properties.sortingKey.slice(0, 30)}...`
                : properties.sortingKey}
            </span>
          ) : (
            <Loader size="xs" />
          )}
        </div>

        <div className="property">
          <span className="property-name">Primary key</span>
          {properties.primaryKey !== null ? (
            <span title={properties.primaryKey} className="property-value">
              {properties.primaryKey.length > 30
                ? `${properties.primaryKey.slice(0, 30)}...`
                : properties.primaryKey}
            </span>
          ) : (
            <Loader size="xs" />
          )}
        </div>

        <div className="property">
          <span className="property-name">Total rows</span>
          {properties.totalRows !== null ? (
            properties.totalRows
          ) : (
            <Loader size="xs" />
          )}
        </div>

        <div className="property">
          <span className="property-name">Size</span>
          {properties.totalBytes !== null ? (
            properties.totalBytes
          ) : (
            <Loader size="xs" />
          )}
        </div>

        <div className="property-last">
          <span className="property-name">Size uncompressed</span>
          {properties.totalBytesUncompressed !== null ? (
            properties.totalBytesUncompressed
          ) : (
            <Loader size="xs" />
          )}
        </div>
      </div>
    </Panel>
  );
};

export const CodeBlockComponent = ({ datasource, table, language = "sql" }) => {
  const [code, setCode] = useState("Loading...");
  if (
    datasource === "clo" &&
    (table === "access_logs" || table === "general_logs")
  ) {
    table = `${table}_v2`;
  }

  useEffect(() => {
    const fetchCreateTableSQL = async () => {
      if (!datasource || !table) return;
      try {
        let tableName = table;

        const requestBody = {
          user_id: getUserId(),
          datasource: datasource,
          table: tableName,
        };

        const response = await chPostRequest("table_ddl", requestBody);
        const res = response.data[0]?.statement;

        if (res) {
          const formattedCode = res.replace(/\\n/g, "\n");
          setCode(formattedCode);
        }
      } catch (error) {
        console.error("Error fetching 'create table' SQL:", error);
      }
    };

    fetchCreateTableSQL();
  }, [datasource, table]);

  const copyToClipboard = () => {
    navigator.clipboard
      .writeText(code)
      .catch((err) => console.error("Failed to copy: ", err));
  };

  return (
    <Panel header="Create Table SQL">
      <div className="code-container">
        <div className="copy-button-wrapper">
          <IconButton
            appearance="subtle"
            icon={<CopyIcon />}
            onClick={copyToClipboard}
          />
        </div>
        <CodeBlock
          shouldWrapLongLines
          text={code}
          language={language}
          showLineNumbers={true}
        />
      </div>
    </Panel>
  );
};

export const addTab = (tabs, setTabs, setActiveKey, content, selectedDatasource, setEditorPanelDatasource, silent = false) => {
  if (document.activeElement && document.activeElement.tagName === 'INPUT') {
    document.activeElement.blur();
  }

  const uniqueKey = `tab-${Date.now()}`;

  const existingNumbers = tabs
    .map((tab) => {
      const match = tab.title.match(/^Untitled Editor (\d+)$/);
      return match ? parseInt(match[1], 10) : null;
    })
    .filter((num) => num !== null);

  let editorNumber;
  if (existingNumbers.length === 0) {
    editorNumber = 1;
  } else {
    editorNumber = Math.max(...existingNumbers) + 1;
  }

  const newTab = {
    key: uniqueKey,
    title: `Untitled Editor ${editorNumber}`,
    content: content,
    existingContent: content,
    datasource: selectedDatasource
  };

  setTabs([...tabs, newTab]);
  if (!silent) {
    setEditorPanelDatasource(selectedDatasource);
    setActiveKey(uniqueKey);
  }
};

const copyTextToClipboard = async (text, label = "Value", toaster = null) => {
  const normalized = typeof text === "string" ? text.trim() : "";
  if (!normalized) return;
  const normalizedLabel = typeof label === "string" ? label : "Value";

  const showToast = (type, body) => {
    if (!toaster) return;
    toaster.push(
      <Message type={type} showIcon closable>
        {body}
      </Message>,
      { placement: "topCenter", duration: 2000 }
    );
  };

  try {
    await navigator.clipboard.writeText(normalized);
    showToast("success", `${normalizedLabel} copied`);
  } catch (err) {
    console.error("Failed to copy text:", err);
    showToast("error", `Unable to copy ${normalizedLabel.toLowerCase()}`);
  }
};

export const removeTab = (
  tabs,
  setTabs,
  activeKey,
  setActiveKey,
  targetKey
) => {
  const filteredTabs = tabs.filter((tab) => tab.key !== targetKey);
  setTabs(filteredTabs);
  if (activeKey === targetKey && filteredTabs.length) {
    setActiveKey(filteredTabs[0].key);
  }
};

export const runQuery = async (
  tabKey,
  editorRefs,
  loading,
  setLoading,
  setResult,
  setError,
  editorPanelDatasource,
  setEditorHeight,
  setResultStats,
  setActiveIconDown,
  progress,
  setProgress,
  cancelTokenSource,
  setCancelTokenSource,
  setResults = null,
  setActiveResultTab = null,
  isRunAll = false,
  queryHistoryRef = null
) => {
  if (loading) {
    if (cancelTokenSource) {
      cancelTokenSource.cancel("Query canceled by the user.");
      setLoading(false);
    }
    return;
  }

  if (!editorPanelDatasource) {
    setError("Please select a datasource.");
    setResult(null);
    if (setResults) {
      setResults(null);
    }
    return;
  }

  const cancelToken = axios.CancelToken.source();
  setCancelTokenSource(cancelToken);

  if (!editorRefs.current[tabKey]) return;


  const editor = editorRefs.current[tabKey];
  const model = editor.getModel();
  const selection = editor.getSelection();

  const stmtRanges = findSqlStatementRanges(model);
  
  let query;
  let executedRanges = [];
  
  if (isRunAll) {
    const totalLines = model.getLineCount();
    const lastLineColumn = model.getLineMaxColumn(totalLines);
  
    editor.setSelection({
      startLineNumber: 1,
      startColumn: 1,
      endLineNumber: totalLines,
      endColumn: lastLineColumn,
    });
  
    query = model.getValue().trim();
    executedRanges = stmtRanges;
  } else if (!selection.isEmpty()) {
    const selectedText = model.getValueInRange(selection).trim();

    if (selectedText) {
      query = selectedText;
      
      const selectionStart = model.getOffsetAt({
        lineNumber: selection.startLineNumber,
        column: selection.startColumn,
      });
      const selectionEnd = model.getOffsetAt({
        lineNumber: selection.endLineNumber,
        column: selection.endColumn,
      });
      
      executedRanges = stmtRanges.filter(r => {
        const rangeStart = model.getOffsetAt({ lineNumber: r.startLine, column: r.startCol });
        const rangeEnd = model.getOffsetAt({ lineNumber: r.endLine, column: r.endCol });
        return rangeStart >= selectionStart && rangeEnd <= selectionEnd;
      });
      
      if (executedRanges.length === 0) {
        executedRanges = [
          {
            startLine: selection.startLineNumber,
            startCol: selection.startColumn,
            endLine: selection.endLineNumber,
            endCol: selection.endColumn,
          },
        ];
      }
    } else {
      const cursorLine = selection.startLineNumber;
      executedRanges = stmtRanges.filter(
        (r) => cursorLine >= r.startLine && cursorLine <= r.endLine
      );

      if (executedRanges.length) {
        const hit = executedRanges[0];
        editor.setSelection({
          startLineNumber: hit.startLine,
          startColumn: hit.startCol,
          endLineNumber: hit.endLine,
          endColumn: hit.endCol,
        });

        query =
          model
            .getValueInRange({
              startLineNumber: hit.startLine,
              startColumn: hit.startCol,
              endLineNumber: hit.endLine,
              endColumn: hit.endCol,
            })
            .trim();
      } else {
        query = model.getLineContent(cursorLine).trim();
      }
    }
  } else {
    const cursorLine = selection.startLineNumber;
    executedRanges = stmtRanges.filter(
      (r) => cursorLine >= r.startLine && cursorLine <= r.endLine
    );

    if (executedRanges.length) {
      const hit = executedRanges[0];
      editor.setSelection({
        startLineNumber: hit.startLine,
        startColumn: hit.startCol,
        endLineNumber: hit.endLine,
        endColumn: hit.endCol,
      });

      query =
        model
          .getValueInRange({
            startLineNumber: hit.startLine,
            startColumn: hit.startCol,
            endLineNumber: hit.endLine,
            endColumn: hit.endCol,
          })
          .trim();
    } else {
      query = model.getLineContent(cursorLine).trim();
    }
  }

  if (!query) {
    message.error({
      content:
        "Please select a query or place the cursor on a line with a query.",
      duration: 3,
    });
    return;
  }

  query = query.replace(/;+\s*$/, "");

  const queries = query
    .split(";")
    .map((q) => q.trim())
    .filter((q) => q.length > 0);
  const isMultipleQueries = queries.length > 1;

  const queryPositions = [];
  executedRanges.forEach(r => {
    const startOffset = model.getOffsetAt({ lineNumber: r.startLine, column: r.startCol });
    const endOffset = model.getOffsetAt({ lineNumber: r.endLine, column: r.endCol });
    queryPositions.push({ startPos: startOffset, endPos: endOffset });
  });

  const processedQueries = queries.map((singleQuery) => {
    const lowerQuery = singleQuery.toLowerCase();

    if (
      !lowerQuery.startsWith("show create") &&
      !lowerQuery.startsWith("describe") &&
      !lowerQuery.startsWith("explain") &&
      !lowerQuery.startsWith("system")
    ) {
      if (!lowerQuery.includes("limit")) {
        singleQuery += " LIMIT 10000";
      }
    }

    if (!/format\s+json$/i.test(singleQuery)) {
      singleQuery += " FORMAT JSON";
    }

    return singleQuery;
  });

  setLoading(true);
  setResult(null);
  setError(null);
  if (setResults) setResults(null);
  setActiveIconDown("down");

  try {
    let requestBody;
    if (isMultipleQueries) {
      requestBody = {
        user_id: getUserId(),
        datasource: editorPanelDatasource,
        queries: processedQueries.map((sql) => ({ sql })),
      };
    } else {
      requestBody = {
        user_id: getUserId(),
        datasource: editorPanelDatasource,
        sql: processedQueries[0],
      };
    }
    const onProgress = (percent) => {
      setProgress(percent);
    };
    const response = await chPostRequest(
      "sql",
      requestBody,
      onProgress,
      cancelToken.token
    );

    if (response.results && Array.isArray(response.results)) {
      if (setResults) {
        const resultsWithPositions = response.results.map((result, index) => ({
          ...result,
          __id: `result_${Date.now()}_${index}`,
          queryPosition: queryPositions[index] || { startPos: 0, endPos: 0 },
        }));
        setResults(resultsWithPositions);
        if (setActiveResultTab && resultsWithPositions.length > 0) {
          setActiveResultTab(resultsWithPositions[0].__id);
        }
      }

      const firstSuccessfulResult = response.results.find(
        (r) => r.status === 200
      );
      const statistics = firstSuccessfulResult?.body?.statistics || {};
      const duration = `${statistics.elapsed?.toFixed(3) || 0}s`;
      const rows = statistics.rows_read?.toString() || "0";
      const size = formatBytes(statistics.bytes_read || 0);

      const queryHistoryPayload = {
        user_id: getUserId(),
        timestamp: new Date().toISOString(),
        query:
          response.results.length > 1
            ? processedQueries.join("; ")
            : processedQueries[0],
        datasource: editorPanelDatasource,
        response: firstSuccessfulResult ? "200" : "400",
        duration: duration,
        size: size,
        rows: rows,
      };

      await queryPostRequest("post_query_history", queryHistoryPayload);

      if (queryHistoryRef) {
        queryHistoryRef.current = [
          { query: queryHistoryPayload.query, datasource: queryHistoryPayload.datasource },
          ...queryHistoryRef.current,
        ];
      }

      setResult(null);
      setResultStats(null);
    } else {
      const statistics = response.statistics || {};
      const duration = `${statistics.elapsed?.toFixed(3) || 0}s`;
      const rows = statistics.rows_read?.toString() || "0";
      const size = formatBytes(statistics.bytes_read || 0);

      const queryHistoryPayload = {
        user_id: getUserId(),
        timestamp: new Date().toISOString(),
        query: isMultipleQueries
          ? processedQueries.join("; ")
          : processedQueries[0],
        datasource: editorPanelDatasource,
        response: "200",
        duration: duration,
        size: size,
        rows: rows,
      };

      await queryPostRequest("post_query_history", queryHistoryPayload);

      if (queryHistoryRef) {
        queryHistoryRef.current = [
          { query: queryHistoryPayload.query, datasource: queryHistoryPayload.datasource },
          ...queryHistoryRef.current,
        ];
      }

      setResult(response.data);
      setResultStats(response.statistics);
      if (setResults) {
        setResults(null);
      }
    }
  } catch (error) {
    console.error("Error running the query:", error);
    const queryHistoryPayload = {
      user_id: getUserId(),
      timestamp: new Date().toISOString(),
      query: isMultipleQueries
        ? processedQueries.join("; ")
        : processedQueries[0],
      datasource: editorPanelDatasource,
      response: "400",
      duration: "0s",
      size: "0 Bytes",
      rows: "0",
    };

    await queryPostRequest("post_query_history", queryHistoryPayload);

    if (queryHistoryRef) {
      queryHistoryRef.current = [
        { query: queryHistoryPayload.query, datasource: queryHistoryPayload.datasource },
        ...queryHistoryRef.current,
      ];
    }

    const errorMessage =
      error.response?.data?.message ||
      error.response?.data ||
      error.message ||
      "An error occurred while processing your query.";
    setError(errorMessage);
    setResult(null);
    if (setResults) {
      setResults(null);
    }
    setProgress(0);
  } finally {
    setLoading(false);
    setTimeout(() => setProgress(0), 1000);
    setCancelTokenSource(null);
  }
};

export const handleTableDropdown = async (
  tableNode,
  selectedDatasource,
  tableTrees,
  setTableTrees,
  progress,
  setProgress,
  toaster
) => {
  try {
    const requestBody = {
      user_id: getUserId(),
      datasource: selectedDatasource,
      table: tableNode.label,
    };
    const onProgress = (percent) => {
      setProgress(percent);
    };
    let tableName = tableNode.label;

    const [metadata, primaryKeyColumns] = await Promise.all([
    chPostRequest("table_meta", requestBody, onProgress),
    chPostRequest("primary_key", { 
      datasource: selectedDatasource, 
      table: tableName 
    }, onProgress),]);

    const tableChildren = (metadata.data || [])
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((column, index) => {
        let columnType = column.type;
        if (columnType.startsWith("Enum8")) {
          columnType = "Enum8";
        } else if (columnType.startsWith("AggregateFunction")) {
          columnType = "AggregateFunction";
        } else if (columnType.startsWith("LowCardinality")) {
          columnType = "LowCard String";
        }

        const isPrimaryKey = primaryKeyColumns.includes(column.name);

        return {
          label: (
            <span
              className="sidebar-table-columns"
              title={`${column.name}  ${columnType}`}
              onDoubleClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                copyTextToClipboard(column.name, "Column name", toaster);
              }}
            >
              <span className="column-icon">
                {isPrimaryKey ? "🗝️" : ""}
              </span>
              <span
                className="column-name"
                title={column.name + "  " + columnType}
              >
                {column.name}
              </span>
              <span
                className="column-type"
                title={column.name + "  " + columnType}
              >
                {columnType}
              </span>
            </span>
          ),
          value: `${tableNode.label}.${column.name}`,
          key: `column-key-${index}`,
        };
      });

    const updatedTableTrees = tableTrees.map((tree, treeIndex) =>
      tree.value === tableNode.value
        ? {
            ...tree,
            children: tableChildren.map((child, childIndex) => ({
              ...child,
              key: `tree-${treeIndex}-child-${childIndex}`,
            })),
          }
        : tree
    );

    setTableTrees(updatedTableTrees);
  } catch (error) {
    console.error("Error fetching table metadata:", error);
    setProgress(0);
  } finally {
    setTimeout(() => setProgress(0), 1000);
  }
};

export const handleDatasourceSelect = async (
  value,
  setTableTrees,
  setSelectedDatasource,
  setEditorPanelDatasource,
  isQueryEditorPanel,
  setTablesCount,
  progress,
  setProgress,
  tabs,
  setTabs,
  activeKey,
  setDictionaries = null,
  setDatasourceTablesMap = null
) => {
  if (!value) {
    setTableTrees([]);
    setSelectedDatasource(null);
    setEditorPanelDatasource(null);
    if (setDictionaries) setDictionaries([]);
    return;
  }

  try {
    const requestBody = {
      user_id: getUserId(),
      datasource: value,
    };
    const onProgress = (percent) => {
      setProgress(percent);
    };

    let newTableTrees = [];
    let dictionaries = [];
    let viewMVMap = {};
    const data = await chPostRequest("table", requestBody, onProgress);

    if (!isQueryEditorPanel) {
      const [view_mv, dicts] = await Promise.all([
          fetchViewAndMVs(value, data.data),
          setDictionaries ? fetchDictionaries(value) : Promise.resolve([])
      ]);

      dictionaries = dicts;
      const viewMVArray = view_mv?.data || view_mv || [];
      viewMVArray.forEach(item => {
        viewMVMap[item.table] = { is_mv: item.is_mv, engine_table: item.engine_table };
      });

      if (setDictionaries) {
        setDictionaries(dictionaries);
      }
    }

    newTableTrees = data.data.map((item, index) => ({
      label: item.name,
      value: `table-${index}`,
      key: `table-key-${index}`,
      children: [],
      ...(dictionaries.length && { isDictionary: dictionaries.includes(item.name) }),
      ...(Object.keys(viewMVMap).length && { isView: viewMVMap[item.name]?.is_mv || false }),
    }));
    
    if (setDatasourceTablesMap) {
      setDatasourceTablesMap((prevMap) => ({
        ...prevMap,
        [value]: newTableTrees,
        [`${value}_viewMap`]: viewMVMap
      }));
    }
    
    if (isQueryEditorPanel)
    {
      setEditorPanelDatasource(value);
      setTabs((prevTabs) =>
        prevTabs.map((tab) =>
          tab.key === activeKey ? { ...tab, datasource: value } : tab
        )
      );
    }
    else
    {
      setSelectedDatasource(value);
      setTablesCount(data.data.length);
      setTableTrees(newTableTrees);
      if (tabs && tabs.find((tab) => tab.key === activeKey)?.datasource === null) {setEditorPanelDatasource(value);}
    }
  } catch (error) {
    console.error("Error fetching table data:", error);
    setProgress(0);
  } finally {
    setTimeout(() => setProgress(0), 1000);
  }
};

export const closePanel = (setPanelOpen) => {
  setPanelOpen(false);
};

export const handleToggle = (checked, setIsToggled) => {
  setIsToggled(checked);
};

export const handleChange = (key, setActiveKey, tabs, setEditorPanelDatasource) => {
  setActiveKey(key);
  const activeTab = tabs.find((tab) => tab.key === key);
  const activeTabDatasource = activeTab ? activeTab.datasource : null;
  setEditorPanelDatasource(activeTabDatasource);
};

export const handleNodeClick = (
  node,
  event,
  clickTimeout,
  selectedDatasource,
  setEditorPanelDatasource,
  tabs,
  setTabs,
  setActiveKey,
  setSelectedTable,
  setSelectedTableDatasource,
  panelOpen,
  setPanelOpen,
  setQueryHistoryOpen,
  setResult,
  setResults,
  setError,
  setEditorContent,
  setResultStats,
  setLoading,
  setActiveIconDown,
  setActiveIconRight,
  isRightToggled,
  setIsRightToggled,
  progress,
  setProgress,
  cancelTokenSource,
  setCancelTokenSource,
  toaster
) => {
  clearTimeout(clickTimeout.current);

  if (node.value.startsWith("table-") && event.detail === 2) {
    event.preventDefault();
    clearTimeout(clickTimeout.current);
    setPanelOpen(false); // close the panel if it is already open
    setTimeout(() => {
      handleTableSelect(
        node,
        selectedDatasource,
        setSelectedTable,
        setSelectedTableDatasource,
        setActiveIconRight,
        setIsRightToggled,
        setPanelOpen,
        setQueryHistoryOpen,
        toaster
      );
    }, 150);
  } else if (node.value.startsWith("table-") && event.detail === 1) {
    clickTimeout.current = setTimeout(() => {
      handleSingleClick(
        node,
        selectedDatasource,
        setEditorPanelDatasource,
        tabs,
        setTabs,
        setActiveKey,
        setResult,
        setResults,
        setError,
        setEditorContent,
        setResultStats,
        setLoading,
        setActiveIconDown,
        progress,
        setProgress,
        cancelTokenSource,
        setCancelTokenSource
      );
    }, 600);
  }
};

const handleSingleClick = async (
  node,
  selectedDatasource,
  setEditorPanelDatasource,
  tabs,
  setTabs,
  setActiveKey,
  setResult,
  setResults,
  setError,
  setEditorContent,
  setResultStats,
  setLoading,
  setActiveIconDown,
  progress,
  setProgress,
  cancelTokenSource,
  setCancelTokenSource
) => {
  const table = node.label;

  try {
    const query = `SELECT * FROM ${selectedDatasource}_maple.${table} LIMIT 10000;`;
    const queryWithFormat = query.replace(/;$/, "") + " FORMAT JSON";

    const existingTab = tabs.find((tab) => tab.content === query);

    if (!existingTab) {
      addTab(tabs, setTabs, setActiveKey, query, selectedDatasource, setEditorPanelDatasource, true);
    }

    setResult(null);
    if (setResults) {
      setResults(null);
    }
    setError(null);
    setActiveIconDown("down");

    setEditorContent(query);
    const metadata = await fetchData(
      selectedDatasource,
      table,
      queryWithFormat
    );
    const data = metadata.data;
    if (data && data.length > 0) {
      setResult(data);
      setResultStats(metadata.statistics);
      setActiveIconDown("down");
    } else {
      setError("No data returned for table.");
      setResult(null);
      if (setResults) {
        setResults(null);
      }
    }
  } catch (error) {
    console.error("Error fetching table metadata:", error);
    const errorMessage =
      (error.response?.data?.message) ||
      error.response?.data ||
      error.message ||
      "Error fetching table metadata.";
    setError(errorMessage);
    setResult(null);
    if (setResults) {
      setResults(null);
    }
  } finally {
    setLoading(false);
    setTimeout(() => setProgress(0), 1000);
    setCancelTokenSource(null);
  }
};

const handleTableSelect = async (
  activeNode,
  selectedDatasource,
  setSelectedTable,
  setSelectedTableDatasource,
  setActiveIconRight,
  setIsRightToggled,
  setPanelOpen,
  setQueryHistoryOpen,
  toaster
) => {
  if (activeNode && activeNode.label && activeNode.children) {
    setPanelOpen(false);
    setSelectedTable(activeNode.label);
    setSelectedTableDatasource(selectedDatasource);
    setQueryHistoryOpen(false);
    setActiveIconRight("right");
    handleToggle(true, setIsRightToggled);
    setPanelOpen(true);
  }
};

export const handleLeftClick = (
  activeIconLeft,
  setActiveIconLeft,
  isToggled,
  setIsToggled
) => {
  setActiveIconLeft(activeIconLeft === "left" ? null : "left");
  handleToggle(!isToggled, setIsToggled);
};

export const handleRightClick = (
  activeIconRight,
  setActiveIconRight,
  isRightToggled,
  setIsRightToggled
) => {
  setActiveIconRight(activeIconRight === "right" ? null : "right");
  handleToggle(!isRightToggled, setIsRightToggled);
};

export const handleDownClick = (
  setIsResultVisible,
  setIsRotated,
  setActiveIconDown
) => {
  setIsResultVisible(v => !v);
  setIsRotated(prev => !prev);
  setActiveIconDown(prev => (prev === "down" ? null : "down"));
};

export const handleEditorDidMount = (
  key,
  editor,
  editorRefs,
  loading,
  setLoading,
  setResult,
  setError,
  editorPanelDatasourceRef,
  setEditorHeight,
  setResultStats,
  setActiveIconDown,
  progress,
  setProgress,
  cancelTokenSource,
  setCancelTokenSource,
  queryHistoryRef,
  monaco,
  setResults = null,
  setActiveResultTab = null,
  datasourceTablesMapRef = null,
) => {
  editorRefs.current[key] = editor;

  const getSuggestionPreference = () => {
    const saved = localStorage.getItem('monaco-query-suggestions-enabled');
    return saved !== null ? JSON.parse(saved) : true;
  };

  const setSuggestionPreference = (enabled) => {
    localStorage.setItem('monaco-query-suggestions-enabled', JSON.stringify(enabled));
  };

  const currentPreference = getSuggestionPreference();
  setSuggestionPreference(currentPreference);

  editor.updateOptions({
    acceptSuggestionOnEnter: "off"
  });

  if (handleEditorDidMount._completionDisposable) {
    handleEditorDidMount._completionDisposable.dispose();
  }

  handleEditorDidMount._completionDisposable = monaco.languages.registerCompletionItemProvider("sql", {
    triggerCharacters: [' ', '.'],
    provideCompletionItems: (model, position) => {
      const suggestions = [];
      const currentDatasource = editorPanelDatasourceRef.current;

      const textBeforeCursor = model.getValueInRange({
        startLineNumber: 1,
        startColumn: 1,
        endLineNumber: position.lineNumber,
        endColumn: position.column,
      });

      const afterFromPattern = /\b(FROM|JOIN|INTO|TABLE|UPDATE)\s+(\w*\.)?(\w*)$/i;
      const isAfterFrom = afterFromPattern.test(textBeforeCursor);
      const isAfterDot = /\b(FROM|JOIN|INTO|TABLE|UPDATE)\s+\w+\.(\w*)$/i.test(textBeforeCursor);

      if (datasourceTablesMapRef && currentDatasource && isAfterFrom) {
        const tablesList = datasourceTablesMapRef.current[currentDatasource] || [];

        if (tablesList.length > 0) {
          if (!isAfterDot) {
            suggestions.push({
              label: `${currentDatasource}_maple.`,
              kind: monaco.languages.CompletionItemKind.Module,
              insertText: `${currentDatasource}_maple.`,
              detail: "Database prefix",
              documentation: `Use ${currentDatasource}_maple prefix for table names`,
              sortText: "0000_" + currentDatasource,
            });
          }

          tablesList.forEach((tableNode) => {
            const tableName = tableNode.label;
            const fullTableName = `${currentDatasource}_maple.${tableName}`;

            if (isAfterDot) {
              suggestions.push({
                label: tableName,
                kind: monaco.languages.CompletionItemKind.Class,
                insertText: tableName,
                detail: `Table from ${currentDatasource}`,
                documentation: `Full table name: ${fullTableName}`,
                sortText: "0001_" + tableName,
              });
            } else {
              suggestions.push({
                label: fullTableName,
                kind: monaco.languages.CompletionItemKind.Class,
                insertText: fullTableName,
                detail: `Table from ${currentDatasource}`,
                documentation: `Full table name: ${fullTableName}`,
                sortText: "0001_" + fullTableName,
              });

              suggestions.push({
                label: tableName,
                kind: monaco.languages.CompletionItemKind.Class,
                insertText: tableName,
                detail: `Table (short name)`,
                documentation: `Table from ${currentDatasource}. Full name: ${fullTableName}`,
                sortText: "0002_" + tableName,
              });
            }
          });
        }

      }

      if (!isAfterFrom && getSuggestionPreference() && queryHistoryRef.current) {
        const filteredHistory = currentDatasource
          ? queryHistoryRef.current.filter((item) => item.datasource === currentDatasource)
          : queryHistoryRef.current;

        const seen = new Set();
        const uniqueHistory = filteredHistory.filter((item) => {
          if (seen.has(item.query)) return false;
          seen.add(item.query);
          return true;
        });

        const lineContent = model.getLineContent(position.lineNumber);
        const range = {
          startLineNumber: position.lineNumber,
          startColumn: 1,
          endLineNumber: position.lineNumber,
          endColumn: lineContent.length + 1,
        };

        const historySuggestions = uniqueHistory.map((item, index) => ({
            label: item.query,
            kind: monaco.languages.CompletionItemKind.Snippet,
            insertText: item.query,
            range: range,
            detail: "Query History",
            documentation: `Query from history: ${item.query}`,
            sortText: "zzzz_" + index.toString().padStart(5, '0'),
          }));
        suggestions.push(...historySuggestions);
      }

      return { suggestions, incomplete: true };
    },
  });

  editor.addAction({
    id: "run-query",
    label: "Run Query",
    contextMenuGroupId: "navigation",
    contextMenuOrder: 1,
    keybindings: [monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyR],
    run: () =>
      runQuery(
        key,
        editorRefs,
        loading,
        setLoading,
        setResult,
        setError,
        editorPanelDatasourceRef.current,
        setEditorHeight,
        setResultStats,
        setActiveIconDown,
        progress,
        setProgress,
        cancelTokenSource,
        setCancelTokenSource,
        setResults,
        setActiveResultTab,
        false,
        queryHistoryRef
      ),
    iconClass: "play-outline-icon",
  });

  editor.addAction({
    id: "format-query",
    label: "Format Query",
    contextMenuGroupId: "navigation",
    contextMenuOrder: 2,
    run: () => {
      const model = editor.getModel();
      const value = model.getValue();
      const formatted = format(value);
      model.setValue(formatted);
    },
  });

  editor.addAction({
    id: "run-all",
    label: "Run All",
    contextMenuGroupId: "navigation",
    contextMenuOrder: 3,
    keybindings: [monaco.KeyMod.CtrlCmd | monaco.KeyMod.Shift | monaco.KeyCode.KeyR],
    run: () => 
      runQuery(
      key,
      editorRefs,
      loading,
      setLoading,
      setResult,
      setError,
      editorPanelDatasourceRef.current,
      setEditorHeight,
      setResultStats,
      setActiveIconDown,
      progress,
      setProgress,
      cancelTokenSource,
      setCancelTokenSource,
      setResults,
      setActiveResultTab,
      true,
      queryHistoryRef
    ),
    iconClass: "run-all-icon",
  });

  editor.addAction({
    id: "toggle-query-suggestions",
    label: "Toggle Query Suggestions",
    contextMenuGroupId: "1_modification",
    contextMenuOrder: 1,
    run: () => {
      const currentState = getSuggestionPreference();
      const newState = !currentState;
      setSuggestionPreference(newState);
      
      const messageText = newState ? "Query history suggestions: ON" : "Query history suggestions: OFF";
      message.success({
        content: messageText,
        duration: 2,
      });
    },
  });

editor.updateOptions({ glyphMargin: true });

let glyphDecoIds = [];

function refreshGlyphs() {
  const model = editor.getModel();
  if (!model) return;

  const stmtRanges = findSqlStatementRanges(model);

  const decos = stmtRanges.map((r) => ({
    range: new monaco.Range(r.startLine, 1, r.startLine, 1),
    options: {
      isWholeLine: true,
      glyphMarginClassName: "qe-glyph-play",
      glyphMarginHoverMessage: { value: "Run this query" },
    },
  }));

  glyphDecoIds = editor.deltaDecorations(glyphDecoIds, decos);
}

refreshGlyphs();
let t;
editor.onDidChangeModelContent(() => {
  clearTimeout(t);
  t = setTimeout(refreshGlyphs, 120);
});

editor.onMouseDown((e) => {
  if (e.target.type !== monaco.editor.MouseTargetType.GUTTER_GLYPH_MARGIN) return;
  const pos = e.target.position;
  if (!pos) return;

  const model = editor.getModel();
  if (!model) return;

  const stmtRanges = findSqlStatementRanges(model);
  const hit = stmtRanges.find((r) => r.startLine === pos.lineNumber);
  if (!hit) return;

  editor.setSelection(
    new monaco.Selection(hit.startLine, hit.startCol, hit.endLine, hit.endCol)
  );

  runQuery(
    key,
    editorRefs,
    loading,
    setLoading,
    setResult,
    setError,
    editorPanelDatasourceRef.current,
    setEditorHeight,
    setResultStats,
    setActiveIconDown,
    progress,
    setProgress,
    cancelTokenSource,
    setCancelTokenSource,
    setResults,
    setActiveResultTab,
    false,
    queryHistoryRef
  );
});

};

export const saveQuery = async (tab, setTabs) => {
  const requestBody = {
    user_id: getUserId(),
    query_title: tab.title,
    query: tab.content,
    datasource: tab.datasource,
  };

  try {
    const response = await queryPostRequest("save_query", requestBody);
    setTabs((prevTabs) =>
      prevTabs.map(
        (t) => (t.key === tab.key ? { ...t, existingContent: t.content, existingDatasource: t.datasource, isSavedQuery: true } : t)
      )
    );
    return response;
  } catch (error) {
    console.error("Error saving query:", error);
    throw error;
  }
};

export const handleSavedQueryClick = (
  title,
  queryContent,
  setTabs,
  setActiveKey,
  setEditorPanelDatasource,
  datasource
) => {
  setTabs((prevTabs) => {
    const existingTab = prevTabs.find((tab) => tab.title === title);

    if (existingTab) {
      setActiveKey(existingTab.key);
      setEditorPanelDatasource(existingTab.datasource);
      return prevTabs;
    }
    const newKey = Date.now().toString();
    const newTab = {
      key: newKey,
      title: title,
      content: queryContent,
      existingContent: queryContent,
      datasource: datasource,
      existingDatasource: datasource,
      isSavedQuery: true
    };

    setEditorPanelDatasource(datasource);
    setActiveKey(newKey);
    setTabs((prevTabs) =>
      prevTabs.map((tab) =>
        tab.key === newKey ? { ...tab, datasource: datasource } : tab
      )
    );
    return [...prevTabs, newTab];
  });
};

export const handleSaveQuery = async (
  tabs,
  setTabs,
  activeKey,
  setShowSaveModal,
  toaster
) => {
  const activeTab = tabs.find((tab) => tab.key === activeKey);
  if (!activeTab) return;
  try {
    await saveQuery(activeTab, setTabs);
    setShowSaveModal(false);
    toaster.push(
      <Message type="success" showIcon closable>
        Query saved
      </Message>,
      { placement: "topCenter", duration: 3000 }
    );
  } catch (error) {
    console.error("Error saving query:", error);
    toaster.push(
      <Message type="error" showIcon closable>
        Error saving query
      </Message>,
      { placement: "topCenter", duration: 3000 }
    );
  }
};
export function findSqlStatementRanges(model) {
  const text = model.getValue();
  const lines = text.split(/\r?\n/);

  const isStmtStart = (s) =>
    /^(SELECT|WITH|INSERT|UPDATE|DELETE|CREATE|ALTER|DROP|TRUNCATE|REPLACE|MERGE|SHOW|EXPLAIN|DESCRIBE|OPTIMIZE|SYSTEM|SET)\b/i.test(
      s
    );

  let inBlock = false;
  let inStr = null;

  const ranges = [];
  let curStart = null;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    for (let j = 0; j < line.length; j++) {
      const ch = line[j];
      const nxt = line[j + 1];

      if (inBlock) {
        if (ch === "*" && nxt === "/") {
          inBlock = false;
          j++;
        }
        continue;
      }

      if (inStr) {
        if (ch === "\\" && j + 1 < line.length) {
          j++;
        } else if (ch === inStr) {
          inStr = null;
        }
        continue;
      }

      if (ch === "-" && nxt === "-") break;
      if (ch === "/" && nxt === "*") {
        inBlock = true;
        j++;
        continue;
      }

      if (ch === "'" || ch === '"' || ch === "`") {
        inStr = ch;
        continue;
      }
    }

    const visible = line
      .replace(/\/\*.*?\*\//g, "")
      .replace(/--.*$/, "")
      .trim();

    if (!inBlock && !inStr && visible && isStmtStart(visible)) {
      const lineStartOffset = model.getOffsetAt({ lineNumber: i + 1, column: 1 });
      if (curStart == null) curStart = lineStartOffset;
    }

    if (!inBlock && !inStr && /;(\s*)?$/.test(visible) && curStart != null) {
      const lineEndOffset = model.getOffsetAt({
        lineNumber: i + 1,
        column: line.length + 1,
      });
      ranges.push({ start: curStart, end: lineEndOffset });
      curStart = null;
    }
  }

  if (curStart != null) {
    ranges.push({ start: curStart, end: text.length });
  }

  return ranges.map(({ start, end }) => {
    const s = model.getPositionAt(start);
    const e = model.getPositionAt(end);
    return {
      startLine: s.lineNumber,
      startCol: s.column,
      endLine: e.lineNumber,
      endCol: e.column,
    };
  });
}

export const fetchDictionaries = async (datasource) => {
  try {
    const requestBody = {
      datasource: datasource
    };
    
    const dictionaries = await chPostRequest("dictionaries", requestBody);
    
    console.log('Fetched dictionaries for datasource:', datasource, dictionaries);
    return dictionaries || [];
  } catch (error) {
    console.error('Error fetching dictionaries:', error);
    return [];
  }
};
